import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { CopilotKit } from "@copilotkit/react-core";
import "@copilotkit/react-ui/styles.css";
// Use direct routing components
import { MemoryRouter, Routes, Route } from 'react-router-dom';
import Home from './views/Home';
import Agent from './views/Agent';
import BottomNavigation from './components/layouts/BottomNavigation';
// Import CSS
import "../../assets/css/index.css";
// Import store hooks and adapter
import { useAuthState, useIsAuthenticated, useCurrentUser, useAuthError, useIsLoading } from './store/authStore';
import { msalAdapter } from './store/msalAdapter';
import { DirectOutlookTools } from './components/utils/DirectOutlookTools';
import { ActionsOutlook } from './components/utils/ActionsOutlook';
import { ActionsBookingOrder } from './components/utils/ActionsBookingOrder';
import { ColleaguesOutlook } from './components/utils/ColleaguesOutlook';
// Placeholder components for new routes
const Resources = () => React.createElement("div", { className: "p-4" },
    React.createElement("h1", { className: "text-2xl font-bold" }, "Resources Page"),
    React.createElement("p", { className: "mt-2" }, "This page will display all available resources."));
const Search = () => React.createElement("div", { className: "p-4" },
    React.createElement("h1", { className: "text-2xl font-bold" }, "Search Page"),
    React.createElement("p", { className: "mt-2" }, "Search for specific resources or meeting rooms."));
const FloorPlan = () => React.createElement("div", { className: "p-4" },
    React.createElement("h1", { className: "text-2xl font-bold" }, "Floor Plan Page"),
    React.createElement("p", { className: "mt-2" }, "View interactive floor plans for the building."));
// AnimatedRoutes component with transition effects
const AnimatedRoutes = () => {
    return (React.createElement("div", { className: "tab-transition" },
        React.createElement(Routes, null,
            React.createElement(Route, { path: "/", element: React.createElement(Home, null) }),
            React.createElement(Route, { path: "/resources", element: React.createElement(Resources, null) }),
            React.createElement(Route, { path: "/search", element: React.createElement(Search, null) }),
            React.createElement(Route, { path: "/agent", element: React.createElement(Agent, null) }),
            React.createElement(Route, { path: "/floorplan", element: React.createElement(FloorPlan, null) }))));
};
const Taskpane = () => {
    // Use store hooks instead of local state
    const authState = useAuthState();
    const isAuthenticated = useIsAuthenticated();
    const currentUser = useCurrentUser();
    const authError = useAuthError();
    const isLoading = useIsLoading();
    // Initialize authentication using MSAL adapter
    const initializeAuthentication = async () => {
        var _a, _b, _c;
        try {
            console.log('Initializing authentication with store...');
            // Get login hint from Office context
            const accountType = (_c = (_b = (_a = Office.context) === null || _a === void 0 ? void 0 : _a.mailbox) === null || _b === void 0 ? void 0 : _b.userProfile) === null || _c === void 0 ? void 0 : _c.accountType;
            const loginHint = (accountType === "office365" || accountType === "outlookCom")
                ? Office.context.mailbox.userProfile.emailAddress
                : "";
            // Initialize MSAL adapter (which will update the store)
            await msalAdapter.initialize(loginHint);
            console.log("Authentication initialized successfully");
        }
        catch (error) {
            console.error("Authentication initialization failed:", error);
            // Error handling is done by the MSAL adapter, which updates the store
        }
    };
    // Initialize authentication when component mounts
    useEffect(() => {
        initializeAuthentication();
    }, []);
    // Render loading, error, or the main application based on authentication state
    const renderAuthStateContent = () => {
        // Check if still loading/initializing
        if (isLoading || !authState.isInitialized) {
            return (React.createElement("div", { className: "flex justify-center items-center h-32 p-4" },
                React.createElement("div", { className: "text-center" },
                    React.createElement("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" }),
                    React.createElement("p", { className: "text-gray-600" }, "Initializing application..."))));
        }
        // Check for authentication errors
        if (authError) {
            return (React.createElement("div", { className: "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded m-4" },
                React.createElement("p", { className: "font-bold" }, "Authentication Error"),
                React.createElement("p", { className: "text-sm" }, authError),
                React.createElement("button", { onClick: initializeAuthentication, className: "bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm mt-2" }, "Retry")));
        }
        // Check if authenticated successfully
        if (isAuthenticated && currentUser) {
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "flex-1 overflow-auto content-with-bottom-nav" },
                    React.createElement(AnimatedRoutes, null)),
                React.createElement(BottomNavigation, null)));
        }
        // Fallback - still initializing
        return (React.createElement("div", { className: "flex justify-center items-center h-32 p-4" },
            React.createElement("div", { className: "text-center" },
                React.createElement("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" }),
                React.createElement("p", { className: "text-gray-600" }, "Setting up authentication..."))));
    };
    return (React.createElement(MemoryRouter, { initialEntries: ['/'], initialIndex: 0 },
        React.createElement("div", { className: "flex flex-col h-screen" }, renderAuthStateContent())));
};
// Initialize the component when Office is ready
Office.onReady((info) => {
    if (info.host === Office.HostType.Outlook) {
        const root = document.getElementById('taskpane-root');
        if (root) {
            ReactDOM.createRoot(root).render(React.createElement(React.StrictMode, null,
                React.createElement(CopilotKit
                // runtimeUrl="http://localhost:4000/copilotkit"
                , { 
                    // runtimeUrl="http://localhost:4000/copilotkit"
                    runtimeUrl: "https://agent-api.add-on-company.com/copilotkit" },
                    React.createElement(Taskpane, null),
                    React.createElement(DirectOutlookTools, null),
                    React.createElement(ActionsOutlook, null),
                    React.createElement(ActionsBookingOrder, null),
                    React.createElement(ColleaguesOutlook, null))));
        }
    }
});
export default Taskpane;
